import { useState, useRef } from 'react'
import { Link } from './nav'
import { ExternalLink, Route, Play, Folder } from 'lucide-react'
import { buildSourceLink } from '../lib/guidanceRoutes'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { useAdminMode } from '../hooks/useAdminMode'
import { bugCategoryLabel } from '../../netlify/functions/lib/polish-pass-types'
import './SourceLinkCard.css'

interface SourceMeta {
  journey_name?: string
  journey_id?: string
  test_run_id?: string
  project_name?: string
  polish_category?: string
  [key: string]: unknown
}

interface SourceLinkCardProps {
  sourceType: string
  sourceId: string | null
  projectId: string | null
  sourceMeta?: SourceMeta | null
  link?: string | null
  className?: string
}

// Use canonical category labels so acronyms like SEO retain their capitalization.
export const passTypeLabel = bugCategoryLabel

function sourceTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    bug: 'Bug',
    exploration: 'Exploration',
    test_run: 'Test Run',
    guidance_update: 'Guidance Update',
    task: 'Task',
    manual: 'Manual',
  }
  return labels[type] ?? type.replace('_', ' ')
}

export default function SourceLinkCard({ sourceType, sourceId, projectId, sourceMeta, link, className }: SourceLinkCardProps) {
  const [showCard, setShowCard] = useState(false)
  const timeoutRef = useRef<number | null>(null)
  // The source link (and the test-run row in the hover card) point at the admin-only task-detail
  // page, so they only render as links for admins. Non-admins still see the source label and the
  // journey/project links, just not the task-page link. (Journey/project links aren't task pages.)
  const adminMode = useAdminMode()

  const meta: SourceMeta = sourceMeta ?? {}
  const journeyName = meta.journey_name
  const journeyId = meta.journey_id
  const testRunId = meta.test_run_id
  const projectName = meta.project_name

  const resolvedLink = adminMode
    ? (link !== undefined ? link : buildSourceLink({ source_type: sourceType, source_id: sourceId, project_id: projectId, source_meta: sourceMeta }))
    : null

  let label = sourceTypeLabel(sourceType)
  // Manual updates have no source artifact, so an id suffix ("#manual") would be noise.
  // Some non-manual updates can also lack a source id, so guard against a null/empty value.
  if (sourceType !== 'manual' && sourceId) label += ` #${sourceId.slice(0, 8)}`

  const journeyLink = projectId && journeyId ? `/projects/${projectId}/journeys/${journeyId}` : null
  const testRunLink = adminMode && projectId && testRunId ? taskDetailUrl(projectId, testRunId) : null
  const projectLink = projectId ? `/projects/${projectId}/overview` : null

  const hasCardContent = journeyName || testRunId || projectName

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setShowCard(true)
  }

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => setShowCard(false), 200)
  }

  return (
    <span
      className={`source-link-card ${className || ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid="source-link-card"
    >
      {resolvedLink ? (
        <Link to={resolvedLink} className="source-link-card__link" data-analytics-event="bug-source-link">
          <ExternalLink size={12} />
          {label}
        </Link>
      ) : (
        <span className="source-link-card__text">{label}</span>
      )}
      {showCard && hasCardContent && (
        <div className="source-link-card__popover" data-testid="source-link-popover">
          {projectName && (
            <div className="source-link-card__popover-row">
              <Folder size={12} className="source-link-card__popover-icon" />
              <span className="source-link-card__popover-label">Project</span>
              {projectLink ? (
                <Link to={projectLink} className="source-link-card__popover-value source-link-card__popover-link" data-analytics-event="bug-source-project-link">{projectName}</Link>
              ) : (
                <span className="source-link-card__popover-value">{projectName}</span>
              )}
            </div>
          )}
          {journeyName && (
            <div className="source-link-card__popover-row">
              <Route size={12} className="source-link-card__popover-icon" />
              <span className="source-link-card__popover-label">Journey</span>
              {journeyLink ? (
                <Link to={journeyLink} className="source-link-card__popover-value source-link-card__popover-link" data-analytics-event="bug-source-journey-link">{journeyName}</Link>
              ) : (
                <span className="source-link-card__popover-value">{journeyName}</span>
              )}
            </div>
          )}
          {testRunId && (
            <div className="source-link-card__popover-row">
              <Play size={12} className="source-link-card__popover-icon" />
              <span className="source-link-card__popover-label">Test Run</span>
              {testRunLink ? (
                <Link to={testRunLink} className="source-link-card__popover-value source-link-card__popover-link" data-analytics-event="bug-source-test-run-link">#{testRunId.slice(0, 8)}</Link>
              ) : (
                <span className="source-link-card__popover-value">#{testRunId.slice(0, 8)}</span>
              )}
            </div>
          )}
        </div>
      )}
    </span>
  )
}
